import type { Fen, Move, ReviewPhases, San } from '@chess/api-model';
import type { OpeningBook, Openings } from '@chess/api-openings';
import { endgameStartOf, openingEndOf, type PhasesProvider } from '@chess/api-review';

// The `phases` provider: where a game's stages are. The book says where the
// opening ended, the material where the endgame began; a game the book never
// knew, or a build without the book, leaves the opening by the fallback rule
// (move 12). The review's loader asks it once when a game opens.

/** The stages of a game, read with its opening book; without one (it failed to load), the fallback rule decides. */
export function phasesOf(game: { moves: Move[]; positions: Fen[] }, book: Pick<OpeningBook, 'book'> | undefined): ReviewPhases {
  let lastKnownPly: number | undefined;
  try {
    lastKnownPly = book?.book(game.positions)?.lastKnownPly;
  } catch {
    lastKnownPly = undefined; // the fallback rule decides
  }
  // Who left the book: the side to move at the last known position, when a move followed it.
  const leftBy =
    lastKnownPly !== undefined && lastKnownPly < game.moves.length ? (game.positions[lastKnownPly]?.split(' ')[1] === 'b' ? 'black' : 'white') : undefined;
  return { openingEnd: openingEndOf(game.moves.length, lastKnownPly), endgameStart: endgameStartOf(game.positions), lastKnownPly, leftBy };
}

/** The provider over the openings API: loads the book for the game's first move, then `phasesOf`. */
export function phasesProvider(openings: Promise<Openings> | Openings): PhasesProvider {
  return {
    async phasesOf(game) {
      let book: OpeningBook | undefined;
      try {
        const first: San | undefined = game.moves[0]?.san;
        book = await (await openings).load(first ?? 'e4');
      } catch {
        book = undefined; // no book: the fallback rule decides
      }
      return phasesOf(game, book);
    },
  };
}
